/**
 * Equity Card Service
 *
 * Service layer for equity card rendering, handling data gathering
 * and template resolution.
 *
 * Usage:
 *   import { EquityCardService } from './services/EquityCardService.js';
 *
 *   const cardLines = EquityCardService.renderCardLines(key, userShares, clientData, bases, values, interest);
 *   // cardLines.holdings, cardLines.costbasis, cardLines.uncalled, cardLines.trade, cardLines.subtitle
 */

import { EquityCardFormatter } from '../formatters/EquityCardFormatter.js';
import { giftTransfer } from '../helpers/gift-transfer.js';

// Cost of the shares still held: each incoming lot netted down as its shares moved on
// (the gift-transfer reconciliation behind Cost Basis Details), at the price paid. Null if unknown.
function remainingLotBasis(key, clientData, holder) {
	if (!holder?.userid) return null;
	try {
		const me = holder.userid.toLowerCase();
		const { giftTransactions = [] } = giftTransfer(holder.userid, false, { dataOnly: true, clientData, shareholderData: holder }) || {};
		return giftTransactions
			.filter((t) => t.shareClass === key && String(t.receiver).toLowerCase() === me)
			.reduce((sum, t) => sum + (Number(t.quantity) || 0) * (Number(t.sharePrice) || 0), 0);
	} catch (err) {
		console.warn('remainingBasis: could not reconcile lots for ' + key, err);
		return null;
	}
}

const EquityCardService = {
	/**
	 * Render all template lines for an equity card
	 *
	 * @param {string} key - Share class key (e.g., "C", "A", "ESOP")
	 * @param {Object} userShares - User's share data for this class (uD[key])
	 * @param {Object} clientData - Full client data object
	 * @param {number} bases - Cost basis value for this class
	 * @param {number} values - Current value for this class
	 * @param {number} interest - Accrued interest for this class (for ACC clients)
	 * @param {Object} calculatorResult - Full calculator result (window.lastCalculationResult)
	 * @param {Object} [options] - Additional options
	 * @param {string} [options.stockStatus] - User's stock status for eligibility calculation (e.g., "10", "100", "Associate")
	 * @param {Object} [options.userData] - Full user data object (for configured variables)
	 * @param {Object} [options.aggregates] - Cap table aggregates for this class (totalOwned, shareholderCount, etc.)
	 * @param {string} [options.client] - Client code (e.g., "portblakely") for path-based variable resolution
	 *
	 * Share-count decimal precision: clientData.equity[key].decimalsConfig.shares
	 * overrides clientData.decimalsConfig.shares (client-level default) for this
	 * class's 'number'-typed fields (owned, approved, vested, etc.). Neither set
	 * = default toLocaleString behavior, unchanged from before this option existed.
	 * @returns {Object} - Rendered lines and variable map
	 *   {
	 *     line1: string,  // Primary line (e.g., "You Own: 1,000 shares")
	 *     line2: string,  // Secondary line (e.g., "Cost Basis: $10,000")
	 *     line3: string,  // Tertiary line
	 *     line4: string,  // Additional line
	 *     vars: Object    // Variable map for conditional rendering decisions
	 *   }
	 */
	renderCardLines: function (key, userShares, clientData, bases, values, interest, calculatorResult, options = {}) {
		const equityConfig = clientData.equity?.[key] || {};
		// Only reconcile lots when a template asks for {{remainingBasis}}
		const usesRemainingBasis = ['line1', 'line2', 'line3', 'line4'].some((l) => String(equityConfig[l + '_template'] || '').includes('remainingBasis'));

		// Share-count ('number'-typed field) decimal precision for this class --
		// e.g. ZEPH's fractional share count wants 4 decimals while llproducts'
		// other, dollar-denominated LLC classes don't. Class-level
		// equity[key].decimalsConfig.shares wins; falls back to the client-level
		// clientData.decimalsConfig.shares when the class doesn't set its own.
		// Scoped to just this render call (restored below) so it can't leak into
		// a sibling class's 'number'-typed fields in the same render pass.
		const toDecimals = (v) => {
			if (v === null || v === undefined || v === '') return null;
			const n = Number(v);
			return Number.isFinite(n) && n >= 0 ? Math.trunc(n) : null;
		};
		const sharesDecimals = toDecimals(equityConfig.decimalsConfig?.shares) ?? toDecimals(clientData.decimalsConfig?.shares);
		const priorNumberFormatOptions = EquityCardFormatter.numberFormatOptions;
		if (sharesDecimals !== null) {
			EquityCardFormatter.numberFormatOptions = { minimumFractionDigits: sharesDecimals, maximumFractionDigits: sharesDecimals };
		}

		try {
			// Find per-class calculator data from breakdown
			const calculatorData = calculatorResult?.breakdown?.find((c) => c.classKey === key) || null;

			// Build variable map from user, client, and calculator data.
			// Normalize only the -1 "no data" sentinel (and non-numeric / NaN) to null
			// so templates with {{#if basis}} guards hide the line. Legitimate negative
			// values from net-sale shareholders pass through — the dashboard has
			// rendered those as e.g. "-$760" historically and we preserve parity.
			let normalizedBases;
			if (typeof bases !== 'number' || Number.isNaN(bases) || bases === -1) {
				normalizedBases = null;
			} else {
				normalizedBases = bases;
			}
			const vars = EquityCardFormatter.buildVariableMap({
				userShares: userShares || {},
				equityConfig,
				bases: normalizedBases,
				remainingBasis: usesRemainingBasis ? remainingLotBasis(key, clientData, options.userData) : null,
				values: values || 0,
				interest: interest || 0,
				calculatorData,
				stockStatus: options.stockStatus || null,
				userData: options.userData || null,
				clientData: clientData,
				aggregates: options.aggregates || null,
				classKey: key,
				client: options.client || null,
			});

			return {
				line1: EquityCardFormatter.renderLine('line1', equityConfig, vars),
				line2: EquityCardFormatter.renderLine('line2', equityConfig, vars),
				line3: EquityCardFormatter.renderLine('line3', equityConfig, vars),
				line4: EquityCardFormatter.renderLine('line4', equityConfig, vars),
				vars, // Return vars for conditional rendering decisions in the caller
			};
		} finally {
			EquityCardFormatter.numberFormatOptions = priorNumberFormatOptions;
		}
	},

	/**
	 * Get the default template for a given line
	 * Useful for displaying defaults in admin UI
	 *
	 * @param {string} lineKey - Line key ('line1', 'line2', 'line3', 'line4')
	 * @returns {string} - Default template string
	 */
	getDefaultTemplate: function (lineKey) {
		return EquityCardFormatter.defaultTemplates[lineKey] || '';
	},
};

export { EquityCardService };
